import { Input, Select, UiButton, UiText } from '@shared/ui';

import cls from './MainTabContent.module.css';
import { useProfileForm } from '../../model/useProfileForm';

import type { Profile } from '@entities/profile';

type MainTabContentProps = {
    profile: Profile;
};

export const MainTabContent = ({ profile }: MainTabContentProps) => {
    const {
        t,
        formValues,
        formErrors,
        activityAreaOptions,
        locationOptions,
        isPending,
        updateFormValue,
        handleSubmit,
    } = useProfileForm(profile);

    return (
        <form className={cls.profileForm} onSubmit={handleSubmit}>
            <div className={cls.fields}>
                <div className={cls.field}>
                    <Input
                        label={t('profile.page.main.fields.name.label')}
                        name="profile-name"
                        value={formValues.name}
                        onChange={(event) => updateFormValue('name', event.target.value)}
                        error={formErrors.name}
                    />
                </div>

                <div className={cls.field} role="group" aria-labelledby="profile-activity-label">
                    <UiText id="profile-activity-label" tag="span" className={cls.fieldLabel}>
                        {t('profile.page.main.fields.activityArea.label')}
                    </UiText>
                    <Select
                        id="profile-activity-area"
                        isSmartPosition
                        dropdownMaxHeight={450}
                        value={formValues.fieldActivityId}
                        onChange={(value) => updateFormValue('fieldActivityId', value)}
                        options={activityAreaOptions}
                        isError={Boolean(formErrors.fieldActivityId)}
                        classes={cls.selectControl}
                    />
                    {formErrors.fieldActivityId && (
                        <UiText role="alert" className={cls.fieldError}>
                            {formErrors.fieldActivityId}
                        </UiText>
                    )}
                </div>

                <div className={cls.field} role="group" aria-labelledby="profile-location-label">
                    <UiText id="profile-location-label" tag="span" className={cls.fieldLabel}>
                        {t(
                            profile.context.locationType === 'city'
                                ? 'profile.page.main.fields.city.label'
                                : 'profile.page.main.fields.country.label'
                        )}
                    </UiText>
                    <Select
                        id="profile-location"
                        isSmartPosition
                        dropdownMaxHeight={450}
                        value={formValues.locationId}
                        onChange={(value) => updateFormValue('locationId', value)}
                        options={locationOptions}
                        needSearch
                        isError={Boolean(formErrors.locationId)}
                        classes={cls.selectControl}
                    />
                    {formErrors.locationId && (
                        <UiText role="alert" className={cls.fieldError}>
                            {formErrors.locationId}
                        </UiText>
                    )}
                </div>

                <div className={cls.field}>
                    <label htmlFor="profile-phone">
                        <UiText tag="span" className={cls.fieldLabel}>
                            {t('profile.page.main.fields.phone.label')}
                        </UiText>
                    </label>
                    <Input
                        name="profile-phone"
                        value={formValues.phone}
                        classes={[cls.phoneInput]}
                        disabled
                    />
                </div>
            </div>

            {formErrors.form && (
                <UiText role="alert" className={cls.formError}>
                    {formErrors.form}
                </UiText>
            )}

            <UiButton
                type="submit"
                className={cls.saveButton}
                btnText={t('button.save.text')}
                disabled={isPending}
            />
        </form>
    );
};
